import store from 'store2';
import type { ComputedRef, Ref } from 'vue';
import { computed, reactive, ref, watch } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import type { ZvdZRouteLocation } from '@/plugins/router';
import router, { route } from '@/plugins/router';
import { RouteName } from '@/router/route-names';
import { PortaalToegang } from '@/types';
import { getMsalInstanceShared } from '@/authConfig';

export enum FeatureFlagId {
  vueOnlineStoreIndex = 'Vue-OnlineStore-Index',
  vueSettingsSjablonen = 'Vue-Settings-Sjablonen',
  vueContactConfirmation = 'Vue-Contact-Confirmation',
  beheerZvdZToegangVerzoeken = 'BeheerZvdZ-ToegangVerzoeken',
  WaarschuwingGroepsmailboxBijOpvoerenEmailadres = 'WaarschuwingGroepsmailbox_BijOpvoerenEmailadres',
  DarkMode = 'DarkMode',
  MKBAfpraakbeleid = 'MKB-Afspraakbeleid',
  ChatbotVIC = 'ChatbotVIC',
}

export type FeatureFlag = {
  id: FeatureFlagId;
  isActive: boolean;
  isUserToggleable: boolean;
};

export type AuthorisationData = {
  isOrganisatieEPDinPA: boolean;
  isVervangingAllowed: boolean;
  isMeldingenAllowed: boolean;
  isMijnToegangAllowed: boolean;
  isATP: boolean;
  isActiviteitenAllowed: boolean;
  isAdministratieAllowed: boolean;
  isAdministratiekantoor: boolean;
  isAfsprakenAllowed: boolean;
  isContractenAllowed: boolean;
  isDocumentenAllowed: boolean;
  isFacturenAllowed: boolean;
  // We deliberately omit isFeatureFlagUserToggleableBasedOnUrl, because it does not support page switching in Vue.
  isGebruikersbeheerAllowed: boolean;
  isInstellingenAllowed: boolean;
  isMagAdministratieMeldingenAllowed: boolean;
  isMagAdministratieWerknemerAllowed: boolean;
  isMagWerknemerBewerkenEnVerzuimAllowed: boolean;
  isOnlineStoreAllowed: boolean;
  isRapportageAllowed: boolean;
  isSchadelastbeheersingAllowed: boolean;
  isUwGegevensAllowed: boolean;
  isVerwerkersovereenkomstAkkoordInzienAllowed: boolean;
  isWerknemersAllowed: boolean;
  isZelfplannerAllowed: boolean;
  isZelfserviceAllowed: boolean;
  isSuperAdminAllowed: boolean;
  isMagMeekijkenRapportenVerzoekenAllowed: boolean;
  isMagMeekijkenTotaalVerzoekenAllowed: boolean;
  isOrganisatieProtocolAllowed: boolean;
  isHeeftWerkgevergroepOrWerkgever: boolean;
  isAdmin: boolean;
  isOproepbriefAdmin: boolean;
  isMeekijkRapporteurRolActief: boolean;
  isMagWerknemerToevoegenAllowed: boolean;
  isVerzekeraar: boolean;
};

type BaseLoginStatus = {
  featureFlags: FeatureFlag[];
  mijnUrl: string;
  portaalToegang: PortaalToegang;
  epdUrl: string;
};
type LoginStatusLoggedIn = BaseLoginStatus & {
  isLoggedIn: true;
  autorisatieData: AuthorisationData;
};
type LoginStatusNotLoggedIn = BaseLoginStatus & {
  isLoggedIn: false;
  autorisatieData: undefined;
};
type LoginStatus = LoginStatusLoggedIn | LoginStatusNotLoggedIn;

type ErrorListener = (error: Error) => void;
const errorListeners: ErrorListener[] = [];
export const setErrorListener = (listener: ErrorListener): void => {
  errorListeners.push(listener);
};

const loading = ref<boolean>(false);
const loginStatus = ref<LoginStatus>();
let getLoginDataPromise: Promise<LoginStatus> | undefined;

const fetchLoginStatus = (): Promise<LoginStatus> => {
  return window
    .fetch('/api/LoginStatus', {
      method: 'GET',
    })
    .then(result => {
      if (!result.ok) {
        return Promise.reject(new Error(result.statusText));
      }
      return result.json() as Promise<LoginStatus>;
    });
};

const getLoginData = () => {
  if (getLoginDataPromise) {
    return getLoginDataPromise;
  }
  loading.value = true;

  getLoginDataPromise = fetchLoginStatus()
    .catch(
      () =>
        new Promise<LoginStatus>((resolve, reject) => {
          window.setTimeout(() => {
            fetchLoginStatus()
              .then(resolve)
              .catch((reason: Error) => {
                errorListeners.forEach(listener => {
                  listener(reason);
                });
                reject(reason);
              });
          }, 1000);
        }),
    )
    .then((result: LoginStatus) => {
      loginStatus.value = result;
      loading.value = false;
      return result;
    });
  return getLoginDataPromise;
};

getLoginData();

const isLoggedIn = computed(() => loginStatus.value?.isLoggedIn);

watch(isLoggedIn, loggedIn => {
  // Clear session when login turns false
  if (loggedIn === false) {
    store.session.clear();
  }
});

const writableActiveFeatures = reactive<
  Map<FeatureFlagId, boolean | undefined>
>(
  Object.values(FeatureFlagId).reduce(
    (result, id) => result.set(id, undefined),
    new Map<FeatureFlagId, boolean | undefined>(),
  ),
);

watch(loginStatus, () => {
  Object.values(FeatureFlagId).forEach(id => {
    writableActiveFeatures.set(id, undefined);
  });
  (loginStatus.value?.featureFlags || []).forEach(({ id, isActive }) => {
    writableActiveFeatures.set(id, isActive);
  });
});

export const activeFeature = writableActiveFeatures as Map<
  FeatureFlagId,
  boolean | undefined
>;

export default function useLoginData(): {
  isLoggedIn: ComputedRef<boolean | undefined>;
  loading: Ref<boolean>;
  getLoginData: () => Promise<LoginStatus>;
  autorisatieData: ComputedRef<AuthorisationData | undefined>;
  refreshLoginData: () => Promise<void>;
  msalLogout: () => Promise<void>;
  logOut: (auto?: boolean) => Promise<void>;
  getLogoutRoute: (auto?: boolean) => RouteLocationRaw;
  getFeatureFlag: (id?: FeatureFlagId) => ComputedRef<FeatureFlag | undefined>;
  mijnUrl: ComputedRef<string | undefined>;
  portaalToegang: ComputedRef<PortaalToegang | undefined>;
  epdUrl: ComputedRef<string | undefined>;
} {
  const refreshLoginData = async () => {
    getLoginDataPromise = undefined;
    await getLoginData();
  };

  const loginReturnRoute = computed<ZvdZRouteLocation | undefined>(() => {
    const location = router.resolve({
      name: route.value.name || '',
      query: { ...route.value.query, ReturnUrl: undefined },
    });
    if (
      location.name === RouteName.LoginIndex ||
      location.name === RouteName.HomeIndex
    ) {
      return undefined;
    }
    return location;
  });

  const msalLogout = async () => {
    // Haal de shared instance op IN de functie
    const msalInstanceShared = getMsalInstanceShared();

    const account =
      msalInstanceShared.getActiveAccount() ||
      msalInstanceShared.getAllAccounts()[0];

    if (account) {
      await msalInstanceShared.logoutRedirect({
        account,
        postLogoutRedirectUri: `${window.location.origin}/LoggedOut`,
      });
    }
  };

  const logOut = async () => {
    // First logout from asp.net
    try {
      await window.fetch(`/api/Login/LogOff?_cb_=${Date.now()}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    // Then logout from entra (deze heeft nu geen argumenten meer nodig)
    await msalLogout();
  };

  const getLogoutRoute = (auto?: boolean): RouteLocationRaw => ({
    name: RouteName.Logout,
    query: {
      ReturnUrl: loginReturnRoute.value?.fullPath,
      auto: auto ? '1' : undefined,
    },
  });

  const getFeatureFlag = (id?: FeatureFlagId) =>
    computed(
      () => id && loginStatus.value?.featureFlags.find(flag => flag.id === id),
    );
  const mijnUrl = computed(() => loginStatus.value?.mijnUrl);
  const portaalToegang = computed(() => loginStatus.value?.portaalToegang);
  const epdUrl = computed(() => loginStatus.value?.epdUrl);

  const autorisatieData = computed<AuthorisationData | undefined>(() =>
    loginStatus.value?.autorisatieData
      ? {
          ...loginStatus.value?.autorisatieData,
        }
      : undefined,
  );

  return {
    isLoggedIn,
    loading,
    getLoginData,
    autorisatieData,
    refreshLoginData,
    msalLogout,
    logOut,
    getLogoutRoute,
    getFeatureFlag,
    mijnUrl,
    portaalToegang,
    epdUrl,
  };
}
